/**
 * Gin Rummy bot.
 *
 * - Draw: takes the upcard / top discard when it cuts the best reachable deadwood by at least three points (usually by
 *   completing or extending a meld), otherwise draws from the stock.
 * - Discard: throws the card that leaves the least deadwood, keeping cards that are close to forming melds and avoiding
 *   cards the opponent has been picking up from the discard pile.
 * - Knock: knocks for gin whenever it can, and otherwise as soon as its deadwood drops to the difficulty's limit
 *   (cautious players wait for a lower count).
 * The bot only ever reads its own seat's view.
 */
import { parseCard, type CardId } from '../../core/cards.js';
import { chance, type BotContext, type BotDifficulty, type BotEngine } from '../../bots/types.js';
import type { GinAction, GinLegal, GinView } from './engine.js';
import { analyzeGinHand, ginBestDiscard, ginCardValue } from './melds.js';

/** The deadwood at which each difficulty is willing to knock. */
const KNOCK_AT: Record<BotDifficulty, number> = { BEGINNER: 4, EASY: 6, NORMAL: 8, HARD: 8, EXPERT: 7 };

/** How many other cards in the hand could join this one in a run or set. */
function synergy(hand: readonly CardId[], i: number): number {
  const c = parseCard(hand[i]!);
  const rank = c.rank === 14 ? 1 : c.rank;
  let s = 0;
  hand.forEach((h, j) => {
    if (i === j) return;
    const o = parseCard(h);
    if (o.rank === c.rank) s += 2;
    else if (o.suit === c.suit) {
      const gap = Math.abs((o.rank === 14 ? 1 : o.rank) - rank);
      if (gap === 1) s += 2;
      else if (gap === 2) s += 1;
    }
  });
  return s;
}

/** Penalty for throwing a card the opponent is likely to want (they have been picking up related cards). */
function feedsOpponent(card: CardId, opponentPicks: readonly CardId[]): number {
  const c = parseCard(card);
  const rank = c.rank === 14 ? 1 : c.rank;
  let p = 0;
  for (const pick of opponentPicks) {
    const o = parseCard(pick);
    if (o.rank === c.rank) p += 3;
    else if (o.suit === c.suit && Math.abs((o.rank === 14 ? 1 : o.rank) - rank) <= 2) p += 2;
  }
  return p;
}

interface Choice {
  card: CardId;
  deadwood: number;
}

function bestDiscard(view: GinView, legal: GinLegal, ctx: BotContext): Choice {
  const hand = view.cards;
  const an = analyzeGinHand(hand);
  const opp = view.seat === null ? [] : view.picked[1 - view.seat] ?? [];
  const memory = ctx.profile.memory > 0 ? opp : [];
  let best: Choice | null = null;
  let bestScore = Infinity;
  for (const card of legal.discardable) {
    const i = hand.indexOf(card);
    const dw = an.deadwood(i);
    const score = dw + synergy(hand, i) * 0.9 + feedsOpponent(card, memory) - ginCardValue(card) * 0.01;
    if (score < bestScore) {
      bestScore = score;
      best = { card, deadwood: dw };
    }
  }
  return best!;
}

/** Deadwood reachable after taking `extra` into the hand and discarding the best other card. */
function deadwoodWith(hand: readonly CardId[], extra: CardId): number {
  const all = [...hand, extra];
  const an = analyzeGinHand(all);
  let best = Infinity;
  for (let i = 0; i < hand.length; i++) best = Math.min(best, an.deadwood(i));
  return best;
}

export const ginBot: BotEngine<GinView, GinLegal, GinAction> = {
  decideAction(view, legal, ctx) {
    return this.chooseCard!(view, legal, ctx);
  },

  chooseCard(view, legal, ctx) {
    const hand = view.cards;
    const top = view.discardTop;
    const wantsTop = (): boolean => {
      if (!top) return false;
      if (ctx.difficulty === 'BEGINNER' && chance(ctx, 0.3)) return false;
      if (chance(ctx, ctx.profile.mistakeRate * 0.5)) return ctx.rng.next() < 0.5;
      const current = analyzeGinHand(hand).deadwood();
      return deadwoodWith(hand, top) <= current - 3;
    };
    if (legal.canTakeUpcard || legal.canPassUpcard) return wantsTop() ? { type: 'takeUpcard' } : { type: 'passUpcard' };
    if (legal.canDrawStock || legal.canDrawDiscard) {
      if (legal.canDrawDiscard && wantsTop()) return { type: 'draw', from: 'discard' };
      return { type: 'draw', from: legal.canDrawStock ? 'stock' : 'discard' };
    }
    if (legal.discardable.length === 0) return this.handleTimeout(view, legal, ctx);
    if (chance(ctx, ctx.profile.mistakeRate) && legal.knockable.length === 0) {
      return { type: 'discard', card: legal.discardable[ctx.rng.int(legal.discardable.length)]! };
    }
    const an = analyzeGinHand(hand);
    // Knock: gin whenever possible, otherwise at the difficulty's limit.
    let knock: Choice | null = null;
    for (const card of legal.knockable) {
      const dw = an.deadwood(hand.indexOf(card));
      if (!knock || dw < knock.deadwood) knock = { card, deadwood: dw };
    }
    if (knock && (knock.deadwood === 0 || knock.deadwood <= KNOCK_AT[ctx.difficulty])) return { type: 'knock', card: knock.card };
    return { type: 'discard', card: bestDiscard(view, legal, ctx).card };
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const diff = view.scores[me]! - view.scores[1 - me]!;
    return Math.max(0, Math.min(1, 0.5 + diff / 200));
  },

  calculateRisk(view) {
    return Math.min(1, analyzeGinHand(view.cards.slice(0, 10)).deadwood() / 40);
  },

  handleTimeout(view, legal) {
    if (legal.canTakeUpcard || legal.canPassUpcard) return { type: 'passUpcard' };
    if (legal.canDrawStock) return { type: 'draw', from: 'stock' };
    if (legal.canDrawDiscard) return { type: 'draw', from: 'discard' };
    const locked = view.locked;
    return { type: 'discard', card: ginBestDiscard(view.cards, locked) };
  },
};
